import React, { useState, useEffect, useCallback } from 'react'
import { Button, Card } from 'react-daisyui'
import { API } from '../../utils/api'
import { Api } from '../../utils/apiServer'
import DefaultLogo from "../../assets/default-logo.png"
import DefaultShop from "../../assets/coffee-shop.png"
import { useParams, useNavigate } from 'react-router-dom'
import { AiOutlineEnvironment, AiOutlineInfoCircle, AiOutlineLeft, AiOutlineTag, AiOutlineStar, AiOutlineClockCircle } from "react-icons/ai"
import { BsShop } from "react-icons/bs"

function MerchantDetail() {
    const [merchantData, setMerchantData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    const { id } = useParams()
    const navigate = useNavigate()

    const loadMerchantDetail = useCallback(async () => {
        setLoading(true)
        setError(null)

        const params = {
            access: 'auth',
            method: API.MERCHANT_DETAIL.type,
            url: API.MERCHANT_DETAIL.url,
            reqBody: {
                partner_id: id
            }
        }

        try {
            const response = await Api(params)
            if (response?.status) {
                setMerchantData(response.data)
            } else {
                setError(response?.message || 'Failed to load merchant details')
            }
        } catch (error) {
            console.error('Error loading merchant detail:', error)
            setError('Failed to load merchant details. Please try again.')
        } finally {
            setLoading(false)
        }
    }, [id])

    useEffect(() => {
        loadMerchantDetail()
    }, [loadMerchantDetail])

    // Loading skeleton component
    const LoadingSkeleton = () => (
        <div className="animate-pulse space-y-6">
            <div className="bg-gray-200 h-32 rounded-lg"></div>
            <div className="space-y-3">
                <div className="bg-gray-200 h-4 rounded w-3/4"></div>
                <div className="bg-gray-200 h-4 rounded w-1/2"></div>
                <div className="bg-gray-200 h-20 rounded"></div>
            </div>
        </div>
    )

    // Error state component
    const ErrorState = () => (
        <Card className="bg-gradient-to-r from-red-50 to-red-100 border-2 border-red-200">
            <Card.Body className="text-center py-12">
                <AiOutlineInfoCircle className="text-6xl text-red-400 mx-auto mb-4" />
                <p className="text-red-700 font-medium text-lg">Error Loading Merchant</p>
                <p className="text-red-600 text-sm mt-1">{error}</p>
                <Button
                    onClick={loadMerchantDetail}
                    className="mt-4 bg-red-600 hover:bg-red-700"
                    size="sm"
                >
                    Try Again
                </Button>
            </Card.Body>
        </Card>
    )

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="mx-auto max-w-sm px-5 py-6">
                    <LoadingSkeleton />
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="mx-auto max-w-sm px-5 py-6">
                    <ErrorState />
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="mx-auto max-w-sm px-5 py-6">
                <div className='flex flex-col space-y-6 scrollbar-hide'>

                    {/* Back Button */}
                    <div className="flex items-center space-x-3">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(-1)}
                            className="border-gray-300"
                        >
                            <AiOutlineLeft className="mr-1" />
                            Kembali
                        </Button>
                    </div>

                    {/* Merchant Header */}
                    <Card className="bg-gradient-to-r from-white to-gray-50 shadow-lg border-0 overflow-hidden">
                        <Card.Body className="p-0">
                            <div className="p-6">
                                <div className="flex items-start space-x-4">
                                    {/* Merchant Logo */}
                                    <div className="flex-shrink-0">
                                        <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-blue-100 to-purple-100 flex items-center justify-center overflow-hidden border border-gray-200">
                                            <img
                                                src={merchantData?.logo || DefaultLogo}
                                                alt={merchantData?.name || 'Merchant'}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    e.target.src = DefaultLogo
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Merchant Info */}
                                    <div className="flex-1 min-w-0">
                                        <h1 className="text-xl font-bold text-gray-800 mb-1">
                                            {merchantData?.name || 'Merchant Name'}
                                        </h1>
                                        <p className="text-sm text-gray-500 mb-3">
                                            Partner ID: {merchantData?.partner_id}
                                        </p>

                                        {/* Merchant Stats */}
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 rounded-lg">
                                                <div className="flex items-center space-x-2">
                                                    <AiOutlineTag className="text-blue-600" />
                                                    <div>
                                                        <p className="text-xs text-blue-700 font-medium">Total Voucher</p>
                                                        <p className="text-sm font-bold text-blue-800">
                                                            {merchantData?.voucher_count || 0}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="bg-gradient-to-r from-green-50 to-green-100 p-3 rounded-lg">
                                                <div className="flex items-center space-x-2">
                                                    <AiOutlineEnvironment className="text-green-600" />
                                                    <div>
                                                        <p className="text-xs text-green-700 font-medium">Lokasi</p>
                                                        <p className="text-sm font-bold text-green-800">
                                                            {merchantData?.redeem_merchants?.length || 0}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* Merchant Description */}
                    {merchantData?.desc && (
                        <Card className="bg-white shadow-lg border-0">
                            <Card.Body>
                                <div className="flex items-center space-x-2 mb-3">
                                    <AiOutlineInfoCircle className="text-blue-600" />
                                    <h2 className="text-lg font-semibold text-gray-800">Tentang Merchant</h2>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <div className="text-gray-700 leading-relaxed">
                                        {merchantData.desc}
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* How to Get Points */}
                    {merchantData?.howtogetpoint && (
                        <Card className="bg-white shadow-lg border-0">
                            <Card.Body>
                                <div className="flex items-center space-x-2 mb-3">
                                    <AiOutlineStar className="text-amber-600" />
                                    <h2 className="text-lg font-semibold text-gray-800">Cara Mendapatkan Poin</h2>
                                </div>
                                <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 rounded-lg border border-amber-200">
                                    <div className="text-gray-700 leading-relaxed">
                                        {merchantData.howtogetpoint}
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Redeem Locations */}
                    {merchantData?.redeem_merchants && merchantData.redeem_merchants.length > 0 && (
                        <Card className="bg-white shadow-lg border-0">
                            <Card.Body>
                                <div className="flex items-center space-x-2 mb-4">
                                    <AiOutlineEnvironment className="text-green-600" />
                                    <h2 className="text-lg font-semibold text-gray-800">
                                        Lokasi Penukaran Voucher ({merchantData.redeem_merchants.length})
                                    </h2>
                                </div>

                                <div className="space-y-4">
                                    {merchantData.redeem_merchants.map((location, i) => (
                                        <Card key={i} className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200">
                                            <Card.Body className="p-4">
                                                <div className="flex space-x-3">
                                                    {/* Location Image */}
                                                    <div className="flex-shrink-0">
                                                        <div className="w-16 h-16 rounded-lg bg-white flex items-center justify-center overflow-hidden border border-gray-200">
                                                            <img
                                                                src={location?.image || DefaultShop}
                                                                alt={location?.name || 'Location'}
                                                                className="w-full h-full object-cover"
                                                                onError={(e) => {
                                                                    e.target.src = DefaultShop
                                                                }}
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Location Details */}
                                                    <div className="flex-1 min-w-0">
                                                        <h3 className="font-semibold text-gray-800 mb-1">
                                                            {location?.name || 'Location Name'}
                                                        </h3>
                                                        <div className="flex items-start space-x-2">
                                                            <AiOutlineEnvironment className="text-green-600 mt-0.5 flex-shrink-0" />
                                                            <p className="text-sm text-gray-600 leading-relaxed">
                                                                {location?.address || 'Address not available'}
                                                            </p>
                                                        </div>

                                                        {/* Additional Info if available */}
                                                        {location?.phone && (
                                                            <div className="flex items-center space-x-2 mt-2">
                                                                <AiOutlineClockCircle className="text-blue-600" />
                                                                <span className="text-xs text-gray-500">
                                                                    {location.phone}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Call to Action */}
                    <Card className="bg-gradient-to-r from-blue-500 to-purple-600 text-white border-0">
                        <Card.Body className="text-center py-6">
                            <BsShop className="text-4xl mx-auto mb-3" />
                            <h3 className="text-lg font-bold mb-2">Siap Mengunjungi?</h3>
                            <p className="text-blue-100 text-sm mb-4">
                                Lihat voucher yang tersedia dari {merchantData?.name}
                            </p>
                            <Button
                                onClick={() => navigate(`/voucher?merchant=${merchantData?.partner_id}`)}
                                className="bg-white text-blue-600 hover:bg-gray-100 font-semibold"
                                size="sm"
                            >
                                <AiOutlineTag className="mr-2" />
                                Lihat Voucher
                            </Button>
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    )
}

export default MerchantDetail